import { PublicClientApplication } from "@azure/msal-browser";
import * as Sentry from "@sentry/vue";
import { QueryClient } from "@tanstack/vue-query";
import { CognitoUserSession } from "amazon-cognito-identity-js";
import { Auth } from "aws-amplify";
import { defineComponent } from "vue";
import ProjectRepository from "@/repositories/ProjectRepository";
import UserRepository from "@/repositories/UserRepository";
import { apiClient } from "@/repositories/clients";
import { getConfig } from "@/services/config";

export default defineComponent({
  methods: {
    async signIn(username: string, password: string) {
      await Auth.signIn(username, password);
      await this.setAxiosAuthToken();
      await this.loadUserData();
    },
    async microsoftSignIn(email?: string) {
      const msalConfig = {
        auth: {
          clientId: "83a39a89-6d35-454a-b301-8024a3269300",
          redirectUri: getConfig().MICROSOFT_LOGIN_REDIRECT_URL,
        },
      };
      const msalInstance = new PublicClientApplication(msalConfig);
      await msalInstance.initialize();
      const loginResponse = await msalInstance.loginPopup({ scopes: ["openid", "email"] });
      const tokenEmail =
        (loginResponse.account.idTokenClaims?.email as string) ||
        (loginResponse.account.idTokenClaims?.preferred_username as string) ||
        "";
      if (email && email !== tokenEmail) {
        throw new Error("Different email in logged in account");
      }
      const cognitoUser = await Auth.signIn({
        username: tokenEmail,
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        options: {
          authFlowType: "CUSTOM_WITHOUT_SRP",
        },
      });
      await Auth.sendCustomChallengeAnswer(cognitoUser, loginResponse.idToken);
      await this.setAxiosAuthToken();
      await this.loadUserData();
    },
    async signOut(queryClient: QueryClient) {
      await Auth.signOut();
      this.clearUserData();
      this.clearAxiosAuthToken();
      queryClient.removeQueries();
    },
    async setAxiosAuthToken() {
      const currentSession = await Auth.currentSession();
      apiClient.defaults.headers.common["Authorization"] = `Bearer ${currentSession
        .getAccessToken()
        .getJwtToken()}`;
    },
    clearAxiosAuthToken() {
      apiClient.defaults.headers.common["Authorization"] = "";
    },
    async loadAppUser() {
      const user = await UserRepository.loadAppUser();
      this.$store.commit("setUser", user);
      analytics.identify(user.username, {
        email: user.email,
        name: user.name,
        customer: user.company,
      });
      // this should use logger.setUser()
      // but for some reason importing logger in this file breaks the full app
      // so far unclear why
      if (process.env.NODE_ENV === "production") {
        Sentry.setUser({ email: user.email });
      }
    },
    async loadProjects() {
      const projects = await ProjectRepository.loadProjectsForUser();
      this.$store.commit("setProjects", projects);
    },
    loadUseLiveImageByProjects() {
      try {
        const localStorageKey = `oculai_useLiveImageByProjects_${this.$store.state.user?._id}`;
        const localStorageItem = JSON.parse(window.localStorage.getItem(localStorageKey) || "{}");
        this.$store.commit("setUseLiveImagByProjects", localStorageItem);
      } catch (error) {
        // just ignore
      }
    },
    async loadUserData() {
      await Promise.all([this.loadAppUser(), this.loadProjects()]);
      this.loadUseLiveImageByProjects();
    },
    clearUserData() {
      this.$store.commit("setUser", undefined);
      this.$store.commit("setProjects", undefined);
      this.$store.commit("setUseLiveImagByProjects", {});
      analytics.identify("", {});
      if (process.env.NODE_ENV === "production") {
        Sentry.setUser(null);
      }
    },
    async getCurrentSession() {
      try {
        return await Auth.currentSession();
      } catch (error) {
        if (error !== "No current user") {
          throw error;
        }
      }
    },
    async refreshSession(): Promise<CognitoUserSession | undefined> {
      const currentSession = await this.getCurrentSession();
      if (!currentSession) {
        return undefined;
      }
      const cognitoUser = await Auth.currentAuthenticatedUser();
      const session = await new Promise<CognitoUserSession | undefined>((resolve) => {
        cognitoUser.refreshSession(
          currentSession.getRefreshToken(),
          (error: Error, session?: CognitoUserSession) => {
            if (error) {
              return resolve(undefined);
            }
            resolve(session);
          },
        );
      });
      if (session) {
        await this.setAxiosAuthToken();
        await this.loadUserData();

        // user is on a page, to which she/he has access to
        if (this.currentCustomerName && this.currentSiteId && !this.currentProjectV2) {
          this.$router.replace("/");
        }
      }

      return session;
    },
    saveReturnUrl() {
      const returnUrl = this.$route.path;
      window.sessionStorage.setItem("returnUrl", returnUrl);
    },
    getReturnUrl() {
      const returnUrl = window.sessionStorage.getItem("returnUrl") || "/";
      window.sessionStorage.removeItem("returnUrl");
      return returnUrl;
    },
  },
});
